/**
 * PicksDashboardHero - Dashboard welcome banner shown when no picks are generated yet.
 */

import type { ParlayStats, TrackedParlay } from '@hooks/useTrackedParlays';
import type { League } from '@parlay-picker/shared/league';
import type React from 'react';

interface PicksDashboardHeroProps {
  user: { displayName?: string } | null;
  selectedLeague: League;
  leagueGames: { id?: string }[];
  loadingGames: boolean;
  todayGameCount: number;
  tomorrowGameCount: number;
  trackedParlays: TrackedParlay[];
  trackedParlayStats: ParlayStats;
}

export function PicksDashboardHero({
  user,
  selectedLeague,
  leagueGames,
  loadingGames,
  todayGameCount,
  tomorrowGameCount,
  trackedParlays,
  trackedParlayStats,
}: PicksDashboardHeroProps): React.ReactElement {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  // While games are still loading, an empty leagueGames must not read as
  // "No games scheduled" — that flashes wrong copy before the slate arrives.
  const subtitle = loadingGames
    ? `Loading today's ${selectedLeague} games...`
    : leagueGames.length > 0
      ? `${todayGameCount} ${selectedLeague} game${todayGameCount === 1 ? '' : 's'} tonight${tomorrowGameCount > 0 ? `, ${tomorrowGameCount} tomorrow` : ''}`
      : `No ${selectedLeague} games scheduled today`;

  return (
    <div className="rebrand-dashboard-hero rounded-xl p-6 sm:p-8 relative overflow-hidden">
      <div className="relative z-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          Good {greeting}, {user?.displayName?.split(' ')[0] || 'there'}
        </h2>
        <p className="text-theme-text-secondary text-sm sm:text-base mb-4">{subtitle}</p>
        <div className="flex flex-wrap gap-3">
          {trackedParlays.length > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <div className="text-xs text-theme-text-tertiary">Tracked</div>
              <div className="text-lg font-bold text-white">{trackedParlayStats.total}</div>
            </div>
          )}
          {trackedParlayStats.pending > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <div className="text-xs text-theme-text-tertiary">Pending</div>
              <div className="text-lg font-bold text-theme-warning">
                {trackedParlayStats.pending}
              </div>
            </div>
          )}
          {trackedParlayStats.won > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <div className="text-xs text-theme-text-tertiary">Won</div>
              <div className="text-lg font-bold text-theme-success">{trackedParlayStats.won}</div>
            </div>
          )}
        </div>
      </div>
      {/* Decorative gradient background */}
      <div className="absolute inset-0 bg-linear-to-br from-theme-primary/20 via-transparent to-theme-secondary/10" />
    </div>
  );
}
